import { styled } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { CrossInCircleIcon, TickInCircleIcon } from '../Icons';
import { Select } from '../Select';
import { ISelectOption } from '../Select/types';
import { TextField } from '../TextField';
import { IFormSelectProps, RenderGroupProps } from './types';

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '1rem 1.2rem',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.colors.fog}`,
  fontFamily: theme.typography.fontFamily,
  fontWeight: 900,
}));

const GroupItems = styled('ul')(({ theme }) => ({
  paddingLeft: 0,

  '& .MuiAutocomplete-option': {
    fontSize: '1rem',
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    padding: '1rem 1.2rem',
    borderBottom: `1px solid ${theme.colors.fog}`,
  },
}));

export const FormSelect: React.FC<IFormSelectProps> = ({
  className,
  name,
  rules,
  options,
  selectProps,
  selectInputProps,
  isDisabled,
  onChangeEffect,
  shouldShowAdornment = false,
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      rules={rules}
      name={name}
      control={control}
      render={props => {
        const isError = Boolean(props.fieldState.error);
        const isInteracted =
          props.fieldState.isDirty || props.fieldState.isTouched || props.field.value !== null || isError;
        const EndAdornment = isInteracted ? isError ? <CrossInCircleIcon /> : <TickInCircleIcon /> : null;

        return (
          <Select
            disablePortal
            className={className}
            disableClearable
            options={options}
            value={options.find(option => option.value === props.field.value) || null}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>, option: ISelectOption | null) => {
              onChangeEffect && onChangeEffect();
              if (option == null) {
                return props.field.onChange(null);
              }
              return props.field.onChange(option.value);
            }}
            disabled={isDisabled}
            renderInput={(params: any) => (
              <>
                <TextField
                  helperText={props.fieldState.error?.message}
                  error={isError}
                  {...params}
                  {...{
                    ...selectInputProps,
                    inputProps: {
                      ...params.inputProps,
                      style: { cursor: 'pointer' },
                      readOnly: 'readonly',
                    },
                    InputProps: {
                      ...params.InputProps,
                      ...selectInputProps.InputProps,
                      endAdornment: (
                        <>
                          {shouldShowAdornment && EndAdornment}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              </>
            )}
            renderGroup={(params: RenderGroupProps) => (
              <li key={params.group}>
                <GroupHeader>{params.group}</GroupHeader>
                <GroupItems>{params.children}</GroupItems>
              </li>
            )}
            {...selectProps}
          />
        );
      }}
    />
  );
};
